<template>
  <div class="form-group form-unit">
    <label class="form-unit__label form-unit__label--upper-sm form-unit__label--w-link">
      {{ $t("template.phone_code", "Phone Code") }}
    </label>
    <div class="tw-flex tw-gap-2 tw-justify-center tw-mt-2 phone-input" style="position: relative">
      <input
        v-for="(digit, index) in phoneCodeDigits"
        :key="index"
        :ref="`phoneCodeInput${index}`"
        type="number"
        v-model="phoneCodeDigits[index]"
        class="hide-arrows form-control tw-w-12 tw-h-12 tw-text-center tw-text-lg tw-font-semibold"
        maxlength="1"
        :tabindex="5 + index"
        @input="onDigitInput(index, $event)"
        @keydown="onDigitKeydown(index, $event)"
        @paste="onPaste($event)"
      />
      <div
        v-if="loading"
        class="tw-absolute tw-inset-0 tw-bg-white tw-bg-opacity-70 tw-flex tw-items-center tw-justify-center tw-z-10"
        style="position: absolute; left: 0; top: 0; width: 100%; height: 100%"
      >
        <span class="loader"></span>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";

export default defineComponent({
  name: "PhoneCodeInput",
  props: {
    phoneCodeDigits: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:phoneCodeDigits"],
  methods: {
    onDigitInput(index, event) {
      const value = event.target.value;

      // Only allow digits and limit to one character
      if (!/^\d*$/.test(value)) {
        this.$set(this.phoneCodeDigits, index, "");
        return;
      }

      // Always replace the current digit with the new value (take only the first digit if multiple entered)
      const singleDigit = value.length > 0 ? value.charAt(value.length - 1) : "";

      // Update the digit
      this.$set(this.phoneCodeDigits, index, singleDigit);

      // Clear any previous errors
      this.phoneCodeError = "";

      // Auto-advance to next field if digit entered and not the last field
      if (singleDigit && index < 5) {
        this.focusNextInput(index + 1);
      }

      // Auto-submit if all 6 digits are filled
      if (
        this.phoneCodeDigits.every((digit) => digit !== "") &&
        this.phoneCodeDigits.join("").length === 6
      ) {
        this.autoSubmit();
      }
    },
    onDigitKeydown(index, event) {
      // Handle backspace to move to previous field
      if (event.key === "Backspace" && !this.phoneCodeDigits[index] && index > 0) {
        this.focusPreviousInput(index - 1);
      }

      // Handle arrow keys for navigation
      if (event.key === "ArrowLeft" && index > 0) {
        event.preventDefault();
        this.focusPreviousInput(index - 1);
      } else if (event.key === "ArrowRight" && index < 5) {
        event.preventDefault();
        this.focusNextInput(index + 1);
      }
    },
    onPaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData.getData("text");
      const digits = pastedData.replace(/\D/g, "").slice(0, 6);

      for (let i = 0; i < 6; i++) {
        this.$set(this.phoneCodeDigits, i, digits[i] || "");
      }

      // Focus the next empty field or the last field if all are filled
      const nextEmptyIndex = this.phoneCodeDigits.findIndex((digit) => !digit);
      if (nextEmptyIndex !== -1) {
        this.focusNextInput(nextEmptyIndex);
      } else {
        this.focusNextInput(5);
        // Auto-submit if all fields are filled
        if (digits.length === 6) {
          this.autoSubmit();
        }
      }
    },
    focusNextInput(index) {
      this.$nextTick(() => {
        const inputs = this.$refs[`phoneCodeInput${index}`];
        if (inputs && inputs.length > 0) {
          inputs[0].focus();
        } else if (inputs) {
          inputs.focus();
        }
      });
    },
    focusPreviousInput(index) {
      this.$nextTick(() => {
        const inputs = this.$refs[`phoneCodeInput${index}`];
        if (inputs && inputs.length > 0) {
          inputs[0].focus();
        } else if (inputs) {
          inputs.focus();
        }
      });
    },
    autoSubmit() {
      // Add a small delay to ensure all digits are properly set
      this.$nextTick(() => {
        if (this.phoneCodeDigits.join("").length === 6) {
          this.$emit("submitPhoneCode", this.phoneCodeDigits.join(""));
        }
      });
    },
  },
});
</script>
